import { useState, useEffect } from "react";
import { Container, Button, Nav, Card } from "react-bootstrap";
import { Table } from "shared/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import Modal from "modules/CardHolders/components/Modal/Modal";
import ACTION_TYPES from "common/utils/actionTypes";
import {
  getCardHoldersApi,
  storeCardHolderApi,
  deleteCardHolderApi,
  updateCardHolderApi,
} from "modules/CardHolders/api";
import "style/local-admin.css";
import { ActionButtonGroup } from "shared/ui";
import { getTableColumns } from "../../constants";
import { MODULES } from "shared/config";
import { useCompanySwitchStore } from "../../../CompanySwitch/store/useCompanySwitchStore";

const initialModal = { type: "", item: {} };

const getModuleName = (cardType) => {
  if (cardType === "corporate") {
    return MODULES.CORPORATE_CARDS;
  } else {
    return MODULES.CREDIT_CARDS;
  }
};

const CardHolderPanel = (props) => {
  const [state, setState] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState(initialModal);
  const [errors, setErrors] = useState("");
  const selectedCompany = useCompanySwitchStore(
    (state) => state.selectedCompany ?? null
  );

  const accessors = getTableColumns(getModuleName(props.cardType)).columns.map(
    (column) => column.accessor
  );

  const handleAdd = async (input) => {
    setIsLoading(true);
    setErrors("");
    try {
      const data = input;
      data.company_code = selectedCompany.code;
      data.module_name = getModuleName(props.cardType);
      const response = await storeCardHolderApi(data);

      if (response?.status === 200) {
        await fetchList();
        setModal(initialModal);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setErrors(error);
      setIsLoading(false);
    }
  };

  const handleEdit = async (input) => {
    setIsLoading(true);
    setErrors("");
    try {
      const data = input;
      data.module_name = getModuleName(props.cardType);
      const response = await updateCardHolderApi(modal.item.id, data);

      if (response?.status === 200) {
        await fetchList();
        setModal(initialModal);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setErrors(error);
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setErrors("");
    try {
      await deleteCardHolderApi(modal.item.id);
      await fetchList();
      setModal(initialModal);
    } catch (error) {
      setErrors(error);
      setIsLoading(false);
    }
  };

  const handleModalOpen = (action, list, item) => {
    if (action === ACTION_TYPES.ADD) {
      return setModal({ type: action });
    }

    setModal({ type: action, item: item });
  };

  const handleModalConfirm = (action, item, input) => {
    if (getModuleName(props.cardType) === MODULES.CORPORATE_CARDS) {
      delete input?.supervisor_email;

      if (!input?.responsible_person_email.length) {
        delete input?.responsible_person_email;
      }
    }

    if (getModuleName(props.cardType) === MODULES.CREDIT_CARDS) {
      delete input?.card_holder_name;
    }

    if (action === ACTION_TYPES.ADD) {
      handleAdd(input);
    } else if (action === ACTION_TYPES.EDIT) {
      handleEdit(input);
    } else if (action === ACTION_TYPES.DELETE) {
      handleDelete(item);
    } else {
      console.log("Action type not specified");
    }
  };

  const fetchList = async () => {
    setIsLoading(true);
    setErrors("");
    try {
      const response = await getCardHoldersApi({
        company_code: selectedCompany?.code,
        module_name: getModuleName(props.cardType),
      });
      const stateData = response.data.data;

      setState(stateData);
      setIsLoading(false);
    } catch (error) {
      setErrors(error);
      setIsLoading(false);
    }
  };

  const hideModal = () => {
    setModal(false);
    setErrors("");
  };

  useEffect(() => {
    fetchList();
    //eslint-disable-next-line
  }, [selectedCompany]);

  return (
    <Container
      fluid
      className="w-75"
      style={{
        paddingBottom: 100,
      }}
    >
      <h3 className="text-center">Card Holders</h3>
      <Card className="p-3">
        <Nav.Item className="ms-auto">
          <Button
            onClick={() => handleModalOpen(ACTION_TYPES.ADD)}
            variant="outline-primary"
            size="sm"
            style={{ color: "var(--blue)" }}
            disabled={isLoading}
            className="local-admin-button"
          >
            <FontAwesomeIcon icon={faPlus} className="awesome" />
            Add new
          </Button>
        </Nav.Item>
        <Table
          rows={state}
          loading={isLoading}
          error={errors}
          accessors={accessors}
          config={{ ...getTableColumns(getModuleName(props.cardType)) }}
          paginated={getTableColumns(getModuleName(props.cardType))?.paginated}
          filtered={getTableColumns(getModuleName(props.cardType))?.filtered}
          actions={[
            {
              accessor: "actions",
              action: (el) => (
                <ActionButtonGroup
                  el={el}
                  loading={isLoading}
                  handleModalOpen={handleModalOpen}
                  editable={true}
                />
              ),
            },
          ]}
        />
      </Card>
      {modal && modal.type ? (
        <Modal
          showModal={modal}
          confirmModal={handleModalConfirm}
          hideModal={hideModal}
          type={modal.type}
          item={modal.item}
          list="card-holders"
          loading={isLoading}
          apiErrors={errors}
          moduleName={getModuleName(props.cardType)}
        />
      ) : null}
    </Container>
  );
};

export default CardHolderPanel;
